import { PRODUCT_OFFERING } from '@oskecare/fe-utilities/lib/constants/dataSources';
import {
  PRODUCT_ID_DATA_BUTTON,
  OFFERING_OPERATION_COMPATIBILITY_WITH,
  OFFERING_OPERATION_SHARING_SERVICE_ELIGIBILITY,
  OFFERING_OPERATIONS,
  PRODUCT_ID_E_SIM,
  OFFERING_OPERATION_CONVERGENT_ELIGIBLE_SUBSCRIPTIONS,
  PRODUCT_ID_CHAINED_PREPAID_BENEFIT_INITIAL_OFFER,
  PRODUCT_ID_POSTPAID_CHRISTMAS_OFFER,
  PRODUCT_ID_AUTOMATIC_REFILL,
  PRODUCT_ID_PREPAID_STARTER_PACK_VOICE_OFFER,
  PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER,
} from './constants.js';
import { cacheWithErrorCheck } from '../../helpers.js';
import ProductList from './models/ProductList.js';
import { getIdentityQueryAndCacheKey } from './helpers.js';

export default class Offering {
  /**
   * @param {string} apiUrl
   * @param {Map<string, Promise<*>>} cache
   * @param {function} request
   * @param {CustomerDataManager} customerDataManager
   */
  constructor({ apiUrl, cache, request, customerDataManager }) {
    this._apiUrl = apiUrl;
    this._cache = cache;
    this._request = request;
    this._customerDataManager = customerDataManager;
  }

  /**
   * @param {string} operationType
   * @param {?string} productSpecificationId
   * @param {?string} productId (mainly used for SHS groups)
   * @param {?string} sn
   * @param {?string} billingAccountId
   * @param {?object} additionalQuery
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   * @private
   */
  _getOffering({
    operationType,
    productSpecificationId = null,
    productId = null,
    sn = null,
    billingAccountId = null,
    additionalQuery = null,
    isNonessential = false,
  }) {
    if (!OFFERING_OPERATIONS.includes(operationType)) {
      throw new Error(`Operation must be one of: ${OFFERING_OPERATIONS.join(', ')}`);
    }

    const [identityQuery, cacheKey] = getIdentityQueryAndCacheKey({
      sn,
      billingAccountId,
    });

    return cacheWithErrorCheck(
      this._cache,
      `${operationType}.${cacheKey}${productSpecificationId ? `.productSpecificationId-${productSpecificationId}` : ''}${productId ? `.productId-${productId}` : ''}`,
      () =>
        this._request({
          method: 'GET',
          route: this._apiUrl,
          query: {
            operationType,
            ...identityQuery,
            'productSpecification.id': productSpecificationId,
            'product.id': productId,
            ...additionalQuery,
          },
          headers: {
            'X-Varnish-Cache': 'forceUpdateCache',
          },
          dataSource: PRODUCT_OFFERING,
          isNonessential,
        }).then(data => new ProductList(data)),
    );
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getDataButton({ sn, isNonessential = false }) {
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: PRODUCT_ID_DATA_BUTTON,
      isNonessential,
    });
  }

  /**
   * @param {string} billingAccountId
   * @param {string} productSpecificationId
   * @param {string} productId
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  async getSharingService({ productSpecificationId, productId, isNonessential = false }) {
    const { billingAccountId } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._getOffering({
      operationType: OFFERING_OPERATION_SHARING_SERVICE_ELIGIBILITY,
      billingAccountId,
      productSpecificationId,
      productId,
      isNonessential,
    });
  }

  /**
   * @param {?string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getESim(sn, isNonessential = false) {
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: PRODUCT_ID_E_SIM,
      isNonessential,
    });
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  async getLove(isNonessential = false) {
    const { billingAccountId } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._getOffering({
      operationType: OFFERING_OPERATION_CONVERGENT_ELIGIBLE_SUBSCRIPTIONS,
      billingAccountId,
      isNonessential,
    });
  }

  /**
   * Return eligibility for "chained prepaid benefit" initial offer product. There's only one variant
   * of the initial offer.
   *
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  getChainedPrepaidBenefitInitialOffer(sn) {
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: PRODUCT_ID_CHAINED_PREPAID_BENEFIT_INITIAL_OFFER,
    });
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getPostpaidChristmasOffer(sn, isNonessential) {
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: PRODUCT_ID_POSTPAID_CHRISTMAS_OFFER,
      isNonessential,
    });
  }

  /**
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  async getAutomaticRefill(sn) {
    const { isPrepaid } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: PRODUCT_ID_AUTOMATIC_REFILL,
      additionalQuery: {
        returnAutopayEligibility: isPrepaid,
      },
    });
  }

  /**
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  getPrepaidStarterPackOffer(sn) {
    return this._getOffering({
      operationType: OFFERING_OPERATION_COMPATIBILITY_WITH,
      sn,
      productSpecificationId: `${PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER},${PRODUCT_ID_PREPAID_STARTER_PACK_VOICE_OFFER}`,
    });
  }
}
